import type { UserPublic } from '@accounts/shared';
import { useQuery } from '@tanstack/react-query';
import { type DragEvent, type ReactNode, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { STR } from '../../i18n';
import { listUsers } from '../../lib/api';
import { useBranding } from '../../lib/branding';
import { initials } from '../../lib/format';
import { useNavOrder } from '../../lib/navOrder';
import { queryKeys } from '../../lib/queryKeys';
import { useSession } from '../../lib/session';
import {
  canEnter,
  landingSurface,
  navSurfaces,
  railOwnerPath,
  type SurfaceId,
  surfaceById,
} from '../../lib/surfaces';
import { useLogout } from '../../lib/useLogout';
import { AppearanceControl } from '../../ui/Appearance';
import { RailBrandMark } from '../../ui/BrandMark';
import { NotificationsBell } from '../../ui/NotificationsBell';
import { buttonStyle, primaryButtonStyle } from '../../ui/styles';

import { useAccessCounts } from './access/useAccessCounts';
import { EditUserDialog } from './EditUserDialog';

/**
 * The admin chrome — variation C, the 96px rail with master/detail, which is the design of
 * record. The rail carries the brand mark (a control: it goes to the panel's first screen),
 * the sections, the appearance control and who you are; the ways out sit in the page header
 * beside the primary action, where the design draws Log out. The order is always
 * `primary → Launcher → Log out`, with Log out last on every screen, so the exit never moves
 * under somebody's cursor as they change surface.
 *
 * THE NAV IS DERIVED. `navSurfaces` filters the one surface table by the same capability
 * test the route guard uses, so an entry can never appear for somebody who would be refused
 * on arrival, and a surface can never be reachable without an entry. Drag-reordering permutes
 * that filtered list and nothing else — see `lib/navOrder.ts`.
 */
const LIST_LIMIT = 200;

/** Read by assistive tech, never drawn: the badge's number for a screen reader. */
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

export function AdminShell({
  surfaceId,
  primary,
  children,
}: {
  surfaceId: SurfaceId;
  primary?: { label: string; onClick: () => void } | undefined;
  children: ReactNode;
}) {
  const location = useLocation();
  const session = useSession();
  const branding = useBranding();
  const logoutMutation = useLogout();
  const [editingSelf, setEditingSelf] = useState(false);
  const dragged = useRef<string | null>(null);

  const user = session.data ?? null;
  const brandName = branding.data?.name ?? '';
  const surface = surfaceById(surfaceId);
  const nav = useNavOrder(navSurfaces(user));
  // The Access entry's red count of waiting requests (FINAL demo; spec row 18) — the same
  // read and HR scoping as the hub's own sub-nav, so the two always agree.
  const accessCounts = useAccessCounts(user);

  /*
   * THE WAY OUT OF THE PANEL. Everybody lands on the launcher after signing in,
   * administrators included, so the panel is somewhere you go deliberately — and somewhere
   * you must be able to leave without typing an address. The rail's brand mark goes to the
   * panel's own first screen and the rail's avatar edits you, so neither of them is it.
   *
   * DERIVED, like every other destination in this client: the target is whatever surface the
   * one table marks as the landing, and the link renders IFF `canEnter` passes against that
   * surface's own declared gate — never a hardcoded '/'. A deliberate, recorded divergence
   * from the frozen admin demo, which has no such control because it has no route table.
   */
  const landing = landingSurface();
  const launcher = canEnter(user, landing.capability) ? landing : null;

  /*
   * The rail's avatar edits the signed-in person, and the dialog needs the full record —
   * `GET /api/auth/me` carries the session principal, not the directory fields. So the users
   * list is read only once the avatar is actually pressed, under the SAME cache key the Users
   * screen uses: on that screen it is already there, and on the others it is one read that
   * only ever happens on demand. No admin request is issued to fill a control nobody touched.
   */
  const selfQuery = useQuery({
    queryKey: queryKeys.users,
    queryFn: () => listUsers({ limit: LIST_LIMIT }),
    enabled: editingSelf,
  });
  const selfRecord: UserPublic | null =
    user === null
      ? null
      : (selfQuery.data?.data.find((candidate) => candidate.id === user.id) ?? null);

  function onDrop(event: DragEvent, targetId: string) {
    event.preventDefault();
    const moved = dragged.current;
    dragged.current = null;
    if (moved !== null) nav.move(moved, targetId);
  }

  return (
    <div
      style={{
        // Was `minHeight: '100vh'` — a FLOOR, so anything taller than the viewport grew the
        // whole document and the page itself scrolled underneath the rail's `position:
        // sticky`. Pinned to exactly `100vh` with `overflow: hidden` instead: the document
        // never scrolls at all, and every region below claims its own scroll independently.
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--bg)',
        color: 'var(--text)',
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      {/*
       * `gridTemplateRows: minmax(0,1fr)` is LOAD-BEARING and the whole reason this layout
       * contains itself. Without an explicit row the grid gets ONE IMPLICIT `auto` row, and
       * an `auto` row's base size is MAX-CONTENT — so a 7664px audit log made the row 7664px
       * tall, `height: 100%` on the column below resolved against THAT rather than the
       * viewport, the flex child never had to shrink, and the whole column overflowed the
       * `overflow: hidden` shell above and was silently clipped. `minmax(0,1fr)` gives the
       * row a zero minimum, so it can never exceed the container, and the overflow is forced
       * down to the region that actually declares `overflow-y: auto`.
       */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '96px minmax(0,1fr)',
          gridTemplateRows: 'minmax(0,1fr)',
          height: '100%',
          minHeight: 0,
        }}
      >
        <div
          style={{
            borderRight: '1px solid var(--border)',
            background: 'var(--surface)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            padding: '14px 9px',
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflowY: 'auto',
            boxSizing: 'border-box',
          }}
        >
          <RailBrandMark branding={branding.data} to={surfaceById('applications').path} />

          <nav
            aria-label={STR.shell.sections}
            style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}
          >
            {nav.entries.map((entry) => {
              // The Access hub's sections are surfaces of their own; its ONE entry is current
              // on any of them.
              const current = railOwnerPath(location.pathname) === entry.path;
              return (
                <Link
                  key={entry.id}
                  to={entry.path}
                  className="nav-r"
                  {...(current ? { 'aria-current': 'page' as const } : {})}
                  draggable
                  title={STR.shell.reorder}
                  onDragStart={(event) => {
                    dragged.current = entry.id;
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', entry.id);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={(event) => {
                    onDrop(event, entry.id);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    padding: '7px 2px',
                    borderRadius: 10,
                    textDecoration: 'none',
                  }}
                >
                  <span
                    className="sq"
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 9,
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 12.5,
                      fontWeight: 700,
                      // Anchors the Access badge below; no visual change for any other tile.
                      position: 'relative',
                    }}
                    aria-hidden="true"
                  >
                    {entry.nav?.letter}
                    {entry.id === 'access' && accessCounts.waitingRequests > 0 ? (
                      // The chip's red pair: token-driven, so it reads in every palette x
                      // mode, and the number beside it means colour is never the only signal.
                      <span
                        className="c-red"
                        style={{
                          position: 'absolute',
                          borderWidth: 1,
                          borderStyle: 'solid',
                          top: -5,
                          right: -7,
                          minWidth: 16,
                          height: 16,
                          padding: '0 4px',
                          borderRadius: 999,
                          boxSizing: 'border-box',
                          display: 'grid',
                          placeItems: 'center',
                          fontSize: 9.5,
                          fontWeight: 700,
                          lineHeight: 1,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {accessCounts.waitingRequests > 99 ? '99+' : accessCounts.waitingRequests}
                      </span>
                    ) : null}
                  </span>
                  <span
                    style={{
                      fontSize: 9.5,
                      fontWeight: 650,
                      letterSpacing: '.02em',
                      textAlign: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    {entry.nav?.label}
                    {entry.id === 'access' && accessCounts.waitingRequests > 0 ? (
                      <span style={VISUALLY_HIDDEN}>
                        {STR.shell.waitingRequests(accessCounts.waitingRequests)}
                      </span>
                    ) : null}
                  </span>
                </Link>
              );
            })}
          </nav>

          <div
            style={{
              marginTop: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              width: '100%',
            }}
          >
            <AppearanceControl />
            {/* HR (not an administrator) cannot edit a profile — the PATCH is admin-gated — so
                for HR the avatar is who you are, not a control. Unchanged for administrators. */}
            {user !== null && (user.isHr || user.isIt) && !user.isAdmin ? (
              <span
                title={user.name}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  background: 'var(--accent)',
                  color: 'var(--accent-fg)',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 10.5,
                  fontWeight: 700,
                }}
              >
                {initials(user.name)}
              </span>
            ) : user === null ? null : (
              <button
                type="button"
                title={STR.shell.editSelf(user.name)}
                aria-label={STR.shell.editSelf(user.name)}
                onClick={() => {
                  setEditingSelf(true);
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  background: 'var(--accent)',
                  color: 'var(--accent-fg)',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 10.5,
                  fontWeight: 700,
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                {initials(user.name)}
              </button>
            )}
          </div>
        </div>

        {/*
         * The content column: now viewport-height (matching the rail's own `height: 100vh`)
         * rather than growing with its content. `<header>` is a fixed-size flex item
         * (`flex: none`); the region below it — EVERY screen's actual content, all six —
         * is the wrapper further down, `flex: 1` with `minHeight: 0` so it is allowed to
         * shrink to the space actually left over instead of refusing to (the trap: without
         * `minHeight: 0` a flex child never shrinks below its content's natural height, and
         * the overflow escapes to the document exactly as it does today).
         */}
        {/* `minHeight: 0` for the same reason as the grid row above: a flex container that
            cannot shrink below its content passes the overflow upward instead of down to
            the scroll region. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            height: '100%',
            minHeight: 0,
          }}
        >
          <header
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '16px 24px',
              borderBottom: '1px solid var(--border)',
              background: 'var(--surface)',
              flex: 'none',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: 18, fontWeight: 650, letterSpacing: '-.015em' }}>
                {surface.header?.title(brandName)}
              </h1>
              <p className="tx-subtle" style={{ margin: '2px 0 0', fontSize: 12.5 }}>
                {surface.header?.subtitle(brandName)}
              </p>
            </div>
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flex: 'none',
              }}
            >
              {/* The bell lives in the TOP header, first in the actions row (board ruling,
                  19 Oct): bell → primary → Launcher → Log out, so Log out stays last. */}
              {user === null ? null : <NotificationsBell user={user} placement="launcher" />}
              {primary === undefined ? null : (
                <button
                  type="button"
                  className="prim"
                  onClick={primary.onClick}
                  style={primaryButtonStyle}
                >
                  {primary.label}
                </button>
              )}
              {/* A NAVIGATION, so a <Link> — the same reason the launcher's own launch
                  controls are anchors. It wears the quiet `ghost` treatment beside Log out
                  rather than the primary one: leaving the panel is never the screen's main
                  action. Everything beyond `buttonStyle` is what makes an anchor sit exactly
                  as the buttons beside it do — the measurements themselves are the header's
                  own, shared with Log out rather than restated. */}
              {launcher === null ? null : (
                <Link
                  className="ghost"
                  to={launcher.path}
                  style={{
                    ...buttonStyle,
                    display: 'inline-flex',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    // An accent outline — more than a plain secondary, still clearly not the
                    // primary action. Token-driven so it follows palette x mode; `.ghost`'s
                    // own hover (surface-2) still applies underneath.
                    borderColor: 'var(--accent)',
                    color: 'var(--accent)',
                  }}
                >
                  {STR.shell.launcher}
                </Link>
              )}
              <button
                type="button"
                className="ghost"
                disabled={logoutMutation.isPending}
                onClick={() => {
                  logoutMutation.mutate();
                }}
                style={{ ...buttonStyle, whiteSpace: 'nowrap' }}
              >
                {logoutMutation.isPending ? STR.common.loggingOut : STR.common.logOut}
              </button>
            </div>
          </header>
          {/*
           * THE FALLBACK SCROLL REGION — shared by all six screens, and why Audit and
           * Settings stay fully reachable rather than being clipped at the viewport edge.
           * Its own `overflow-y: auto` only ever ACTIVATES when what a screen renders here
           * is taller than the space available:
           *   - Applications / Users / Grants render `masterDetailStyle` here, which is now
           *     bound to `height: 100%` of exactly this space and never grows past it — its
           *     OWN two columns (`listPaneStyle` / `detailPaneStyle`) carry their own
           *     independent `overflow-y: auto`, so THEY absorb any overflow and this
           *     fallback never has anything left to scroll. Three regions, three scrollbars,
           *     none dragging another out of view.
           *   - Audit / Settings / Announcements render plain, unbounded-height content —
           *     exactly the tall (7664px, 1376px) or short (fits) blocks measured in
           *     Chrome — so THIS div is what scrolls for them, precisely as the page itself
           *     used to. Nothing about their reachability changes; only where the scrollbar
           *     lives does.
           */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</div>
        </div>
      </div>

      {editingSelf && selfRecord !== null ? (
        <EditUserDialog
          user={selfRecord}
          onClose={() => {
            setEditingSelf(false);
          }}
        />
      ) : null}
    </div>
  );
}
